/**
 * ============================================================
 * workspaceSlice.ts — Gerenciamento de Workspaces e Projetos
 * ============================================================
 *
 * Responsabilidade: Controlar o estado de workspaces (espacos de
 *   trabalho), projetos dentro de cada workspace, o perfil do
 *   usuario logado, e a navegacao entre contextos (workspace/projeto
 *   ativo).
 * Arquitetura: Slice do Zustand que faz parte da store global
 *   (AppState). Persiste IDs ativos no localStorage para restaurar
 *   sessao ao recarregar a pagina.
 * Padroes: Zustand Slice, Persistencia seletiva via localStorage,
 *   Reset de cache ao trocar de contexto (evita dados stale).
 * Dependencias:
 *   - Depende de: ../types, ../persistence, @/lib/constants
 *   - Quem depende dele: useStore (composicao), componentes de
 *     workspace/projeto, sidebar, rotas protegidas
 *
 * ============================================================
 */

import type { StateCreator } from 'zustand';
import type { Workspace, Project, ProjectType, UserProfile } from '../types';
import { DEFAULT_KANBAN_COLUMNS } from '@/lib/constants';
import { loadPersisted, persist } from '../persistence';
import { STORAGE_KEYS } from '@/lib/constants';

// ── Interface do Slice ──
// Define todo o estado e acoes relacionadas a workspaces/projetos
export interface WorkspaceSlice {
  workspaces: Workspace[];
  activeWorkspaceId: string;
  activeProjectId: string;
  activeProjectType: ProjectType;
  setActiveWorkspace: (id: string) => void;
  setActiveProject: (id: string, type?: ProjectType) => void;
  /** Reset project-scoped cache (deals, columns, fields). Does NOT touch the database. */
  clearProjectCache: () => void;
  addWorkspace: (ws: Workspace) => void;
  updateWorkspace: (id: string, updates: Partial<Workspace>) => void;
  deleteWorkspace: (id: string) => void;
  addProjectToWorkspace: (wsId: string, project: Project) => void;
  deleteProjectFromWorkspace: (wsId: string, projectId: string) => void;
  userProfile: UserProfile;
  updateUserProfile: (updates: Partial<UserProfile>) => void;
}

// ── Constantes de Reset de Cache ──
// Ao trocar de projeto, dados do projeto anterior devem ser limpos
// para evitar exibir informacoes stale (ex: deals do projeto antigo
// aparecendo antes do carregamento do novo projeto).

/** Campos resetados ao trocar de projeto (previne dados stale do projeto anterior) */
const PROJECT_CACHE_RESET = {
  // `deals`/`dealsLoading` saíram daqui: desde 2026-09-15 a lista de leads
  // vive só no React Query (queryKeys.deals) e a store guarda apenas o
  // selectedDeal. Continuavam sendo zerados campos que não existem mais.
  selectedDeal: null,
  kanbanColumns: DEFAULT_KANBAN_COLUMNS, // Volta para colunas padrao ate carregar do DB
  customFieldDefs: [] as any[],
  syncProgress: 0,
  dateFilter: { preset: '30d' as const },
};

/** Campos resetados ao trocar de workspace (inclui tudo do projeto + membros do time) */
const WORKSPACE_CACHE_RESET = {
  ...PROJECT_CACHE_RESET,
  teamMembers: [] as any[], // Membros sao por workspace, nao por projeto
};

// ── Criacao do Slice ──
export const createWorkspaceSlice: StateCreator<WorkspaceSlice, [], [], WorkspaceSlice> = (set) => ({
  // Estado inicial: workspaces vazio (carregado do DB via React Query)
  workspaces: [],
  // IDs ativos restaurados do localStorage para manter sessao entre reloads
  activeWorkspaceId: loadPersisted(STORAGE_KEYS.ACTIVE_WORKSPACE_ID, ''),
  activeProjectId: loadPersisted(STORAGE_KEYS.ACTIVE_PROJECT_ID, ''),
  activeProjectType: loadPersisted<ProjectType>(STORAGE_KEYS.ACTIVE_PROJECT_TYPE, 'crm'),

  // Os campos zerados moram em outras slices (kanban, ui); o `set` desta
  // slice é tipado só com WorkspaceSlice, daí o cast — mesmo motivo dos
  // casts StateCreator<AppState, ...> em useStore.ts.
  clearProjectCache: () => set(PROJECT_CACHE_RESET as Partial<WorkspaceSlice>),

  // ── Acoes de Troca de Contexto ──

  setActiveWorkspace: (id) => {
    persist(STORAGE_KEYS.ACTIVE_WORKSPACE_ID, id);
    set((state) => {
      // Otimizacao: so reseta cache se realmente mudou de workspace
      if (state.activeWorkspaceId === id) return { activeWorkspaceId: id };
      return { activeWorkspaceId: id, ...WORKSPACE_CACHE_RESET };
    });
  },
  setActiveProject: (id, type) => {
    persist(STORAGE_KEYS.ACTIVE_PROJECT_ID, id);
    if (type) persist(STORAGE_KEYS.ACTIVE_PROJECT_TYPE, type);
    set((state) => {
      // Otimizacao: so reseta cache se realmente mudou de projeto
      if (state.activeProjectId === id) return { activeProjectId: id, ...(type ? { activeProjectType: type } : {}) };
      return { activeProjectId: id, ...(type ? { activeProjectType: type } : {}), ...PROJECT_CACHE_RESET };
    });
  },

  // ── CRUD de Workspaces ──

  addWorkspace: (ws) => set((s) => ({ workspaces: [...s.workspaces, ws] })),
  updateWorkspace: (id, updates) =>
    set((s) => ({
      workspaces: s.workspaces.map((w) => (w.id === id ? { ...w, ...updates } : w)),
    })),
  deleteWorkspace: (id) =>
    set((s) => {
      const remaining = s.workspaces.filter((w) => w.id !== id);
      const newActiveWs = s.activeWorkspaceId === id ? remaining[0]?.id || '' : s.activeWorkspaceId;
      const deletedWs = s.workspaces.find(w => w.id === id);
      const deletedProjectIds = new Set(deletedWs?.projects?.map(p => p.id) || []);
      const newActiveProject = deletedProjectIds.has(s.activeProjectId) ? '' : s.activeProjectId;
      // Persiste novos IDs ativos no localStorage para evitar estado stale após refresh
      persist(STORAGE_KEYS.ACTIVE_WORKSPACE_ID, newActiveWs);
      persist(STORAGE_KEYS.ACTIVE_PROJECT_ID, newActiveProject);
      return { workspaces: remaining, activeWorkspaceId: newActiveWs, activeProjectId: newActiveProject };
    }),
  // ── CRUD de Projetos dentro de Workspaces ──

  addProjectToWorkspace: (wsId, project) =>
    set((s) => ({
      workspaces: s.workspaces.map((w) =>
        w.id === wsId ? { ...w, projects: [...w.projects, project] } : w,
      ),
    })),
  deleteProjectFromWorkspace: (wsId, projectId) =>
    set((s) => {
      // Se o projeto deletado é o ativo, limpa o activeProjectId
      const newActiveProject = s.activeProjectId === projectId ? '' : s.activeProjectId;
      if (s.activeProjectId === projectId) {
        persist(STORAGE_KEYS.ACTIVE_PROJECT_ID, '');
      }
      return {
        workspaces: s.workspaces.map((w) =>
          w.id === wsId ? { ...w, projects: w.projects.filter((p) => p.id !== projectId) } : w,
        ),
        activeProjectId: newActiveProject,
      };
    }),

  // ── Perfil do Usuario ──
  // Perfil local (nao persistido neste slice — vem do DB via React Query)
  userProfile: { name: '', email: '', role: 'admin', tag: '' },
  updateUserProfile: (updates) => set((s) => ({ userProfile: { ...s.userProfile, ...updates } })),
});
